import React, { useCallback } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Heart from '../../../../static/heart.svg';
import HeartFull from '../../../../static/heartFull.svg';
import Location from '../../../../static/location.svg';
import * as styles from './cardAgenda.module.scss';
import { AgendaOtQuery, CardAgendaQuery } from '../../../../graphql-types';
import { Path, usePageContext } from '../../../hooks/contexts/usePageContext';
import { useWishlistContext } from '../../../hooks/contexts/useWishlistContext';
import { LocaleLink } from '../localeLink/LocaleLink';
import { URLS } from '../../../../constants';
import { sanitizeSlug } from '../../../utils/sanitizeSlug';
import { SanityFilters } from '../../../utils/types';

interface ICardAgendaProps {
  ot: AgendaOtQuery['allIngenieOt']['nodes'][0];
  agendaPath?: Path;
  sanityFilters?: SanityFilters;
}

export const isLiked = (e, ot, modifyWishlist) => {
  e.preventDefault();
  modifyWishlist(ot);
};

const months = {
  '01': 'JAN',
  '02': 'FEV',
  '03': 'MAR',
  '04': 'AVR',
  '05': 'MAI',
  '06': 'JUI',
  '07': 'JUIL',
  '08': 'AOU',
  '09': 'SEPT',
  '10': 'OCT',
  '11': 'NOV',
  '12': 'DEC',
};

const computeDates = (ouvertures) => {
  const dayBegin = ouvertures?.[0]?.date_debut.split('-')[2];
  const monthBegin = ouvertures?.[0]?.date_debut.split('-')[1];
  const yearBegin = ouvertures?.[0]?.date_debut.split('-')[0];
  const dayEnd = ouvertures?.[ouvertures.length - 1]?.date_fin.split('-')[2];
  const monthEnd = ouvertures?.[ouvertures.length - 1]?.date_fin.split('-')[1];
  const yearEnd = ouvertures?.[ouvertures.length - 1]?.date_fin.split('-')[0];

  if (ouvertures?.[0]?.date_debut && ouvertures?.[ouvertures.length - 1]?.date_fin) {
    if (ouvertures?.[0]?.date_debut !== ouvertures?.[ouvertures.length - 1]?.date_fin) {
      return yearBegin === yearEnd
        ? `${dayBegin} ${months[monthBegin]} > ${dayEnd} ${months[monthEnd]}`
        : `${dayBegin} ${months[monthBegin]} ${yearBegin} > ${dayEnd} ${months[monthEnd]} ${yearEnd}`;
    }
    return `${dayBegin} ${months[monthBegin]} ${yearBegin}`;
  }
  return null;
};

const capitalize = (str: string) => {
  const lower = str.toLowerCase();
  return str.charAt(0).toUpperCase() + lower.slice(1);
};

export const CardAgenda = ({ ot, sanityFilters }: ICardAgendaProps) => {
  const { lang } = usePageContext();
  const { wishlist, modifyWishlist } = useWishlistContext();
  const { sanityTranslations } = useStaticQuery<CardAgendaQuery>(QUERY);

  const computeCats = useCallback(
    (cats) => {
      const categories: string[] = Object.entries(cats).flatMap(([key, value]) => {
        if (!value) return [];
        const label = sanityFilters?.filters?.find((x) => x.criteria === key);
        return label?._rawLabel[lang] ?? [];
      });
      return categories.join(', ');
    },
    [lang, sanityFilters?.filters]
  );
  const liked = !!wishlist.find((x) => x.id === ot?.id);

  const image = ot.medias?.[0]?.resolutions?.find((resolution) => resolution?.type === '320x240');

  return (
    <LocaleLink
      className={styles.wrapper}
      to={`${URLS.agenda.url}/${sanitizeSlug(ot?.libelle?.fr)}`}
    >
      <div className={styles.hideOverflow}>
        <button
          aria-label="Like"
          type="button"
          onClick={(e) => isLiked(e, ot, modifyWishlist)}
          className={styles.likeWrapper}
        >
          {liked ? <HeartFull className={styles.heart} /> : <Heart className={styles.heart} />}
        </button>
        <div className={styles.content}>
          <div className={styles.imageWrapper}>
            <img alt="tours-tourisme" src={image?.url ?? ''} loading="lazy" />
            <div>
              {ot?.pictosPartners?.map((picto, index) => (
                <div className={styles.logoWrapper} key={picto?.url ?? index}>
                  <img src={picto?.url ?? ''} alt={picto?.alt ?? 'logo placeholder'} />
                </div>
              ))}
            </div>
          </div>
          <div className={styles.data}>
            <div className={styles.date}>{computeDates(ot.ouvertures)}</div>
            <div>
              <div>
                {sanityFilters ? (
                  <div className={styles.cat}>{computeCats(ot?.criteres210)}</div>
                ) : null}
                <div className={styles.title}>{ot?.libelle?.fr}</div>
              </div>
              <div className={styles.location}>
                <Location />
                <div>{capitalize(ot?.localisation?.adresse?.ville ?? '')}</div>
              </div>
            </div>
            <div className={styles.link}>
              {ot?.reservable ? (
                <button type="button" className={styles.buttonLink}>
                  {sanityTranslations?._rawLinkLabel?.[lang]}
                </button>
              ) : (
                <div className={styles.buttonDiscover}>
                  {sanityTranslations?._rawRubriqueButton?.[lang]}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LocaleLink>
  );
};

const QUERY = graphql`
  query CardAgenda {
    sanityTranslations {
      _rawLinkLabel
      _rawRubriqueButton
    }
  }
`;
